import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { Alert, Button, ButtonGroup, Menu, Stack } from '@mantine/core';

import { useTranslation } from 'i18n';

import { logException } from 'helpers/loggers';
import DockedDrawerFormContent from 'primitives/DockedDrawer/DockedDrawerFormContent';
import { CheckCircleIconMd, ChevronDownIconXs, ChevronUpIconXs } from '@chownow/pantry/icons';
import { hasDrawerIntegration, MARKETPLACE_CROP_CONFIG } from './marketplaceConfig';
import type { CropState } from './EditPhotoContent';
import { CroppedImagePreview } from './CroppedImagePreview';
import { MarketplacePreviewCards } from './MarketplacePreviewCards';
import { cropImageToBlob } from './cropImageToBlob';

const NS = 'menuPhotosPhotoDrawer';

type SuccessPhotoContentProps = {
  imageUrl: string;
  // The un-enhanced, un-cropped upload — kept separate from `imageUrl` (which reflects
  // enhancement) so "Original" always downloads the file exactly as the user uploaded it.
  originalImageUrl: string;
  cropState: CropState;
  filename?: string;
  activeIntegrationNames?: Set<string>;
  onDone: () => void;
  onEdit: () => void;
  onReplace: () => void;
};

function downloadViaAnchor(url: string, filename: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export const SuccessPhotoContent: FunctionComponent<SuccessPhotoContentProps> = ({
  imageUrl,
  originalImageUrl,
  cropState,
  filename,
  activeIntegrationNames,
  onDone,
  onEdit,
  onReplace,
}) => {
  const isThirdParty = hasDrawerIntegration(activeIntegrationNames);
  const { t } = useTranslation(NS);
  const [alertDismissed, setAlertDismissed] = useState(false);
  // Menu manages its own open/close state; we only listen in so the trigger button
  // can stay highlighted and flip its caret while the dropdown is open.
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);

  // imageUrl/originalImageUrl are owned by PhotoDrawer — do not revoke them here.
  const handleDownloadOriginal = () =>
    downloadViaAnchor(originalImageUrl, filename ?? 'original.jpg');

  const makeCroppedDownloadHandler =
    (ratio: Parameters<typeof cropImageToBlob>[2], outFilename: string) => () => {
      cropImageToBlob(imageUrl, cropState, ratio)
        .then(file => {
          const url = URL.createObjectURL(file);
          downloadViaAnchor(url, outFilename);
          // Safe to revoke synchronously — browser queues the download on click.
          URL.revokeObjectURL(url);
        })
        .catch((err: unknown) =>
          logException({
            message: 'Failed to download cropped photo',
            error: err,
            context: {},
            tags: {},
          }),
        );
    };

  const handleDownloadCropped = makeCroppedDownloadHandler('4:3', 'cropped.jpg');

  const dockButton = (
    <Button
      fullWidth
      radius="xl"
      tt="uppercase"
      bg="var(--surface-inverse-primary)"
      c="var(--content-inverse-primary)"
      style={{ letterSpacing: 'var(--letterspacing-wide)' }}
      onClick={onDone}
    >
      {t('SuccessPhotoContent.done')}
    </Button>
  );

  return (
    // Type DockedDrawerFormContent (.jsx) to remove this suppression.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <DockedDrawerFormContent stacked dockButtons={dockButton}>
      {/* 1px keeps the ButtonGroup's top border from being clipped at the scroll container edge */}
      <Stack gap="lg" pt="1px">
        {!alertDismissed && (
          <Alert
            variant="success"
            icon={<CheckCircleIconMd />}
            withCloseButton
            onClose={() => setAlertDismissed(true)}
          >
            {t('SuccessPhotoContent.successMessage')}
          </Alert>
        )}

        <ButtonGroup>
          <Button variant="ghost" flex={1} h="xl" onClick={onEdit}>
            {t('SuccessPhotoContent.edit')}
          </Button>
          <Button variant="ghost" flex={1} h="xl" onClick={onReplace}>
            {t('SuccessPhotoContent.replace')}
          </Button>
          <Menu
            position="bottom-end"
            withinPortal
            onOpen={() => setIsDownloadMenuOpen(true)}
            onClose={() => setIsDownloadMenuOpen(false)}
          >
            <Menu.Target>
              <Button
                variant="ghost"
                flex={1}
                h="xl"
                rightSection={isDownloadMenuOpen ? <ChevronUpIconXs /> : <ChevronDownIconXs />}
                bg={isDownloadMenuOpen ? 'var(--surface-paper-3)' : undefined}
              >
                {t('SuccessPhotoContent.download')}
              </Button>
            </Menu.Target>
            <Menu.Dropdown w={200}>
              <Menu.Item onClick={handleDownloadOriginal}>
                {t('SuccessPhotoContent.downloadOriginal')}
              </Menu.Item>
              <Menu.Item onClick={handleDownloadCropped}>
                {t('SuccessPhotoContent.downloadEdited')}
              </Menu.Item>
              {MARKETPLACE_CROP_CONFIG.filter(({ integrationName }) =>
                activeIntegrationNames?.has(integrationName),
              ).map(({ integrationName, outputRatio, filename: cropFilename, downloadKey }) => (
                <Menu.Item
                  key={integrationName}
                  onClick={makeCroppedDownloadHandler(outputRatio, cropFilename)}
                >
                  {t(downloadKey)}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        </ButtonGroup>

        {isThirdParty ? (
          <MarketplacePreviewCards
            imageUrl={imageUrl}
            cropState={cropState}
            activeIntegrationNames={activeIntegrationNames ?? new Set()}
          />
        ) : (
          <CroppedImagePreview imageUrl={imageUrl} cropState={cropState} />
        )}
      </Stack>
    </DockedDrawerFormContent>
  );
};
